import { useMemo, useState } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { TableV8 } from "@xfrontend/xui-react-plus";
import { Settings } from "@xfrontend/xicon";
import HeaderSelectFilter from "#/modules/dashboard/components/HeaderSelectFilter";
import { FilterComponentProps, SelectOption } from "#/modules/dashboard/types";
import { useOrg } from "#/modules/auth/OrgContext";
import { Rbac } from "#/modules/auth/useRbac";
import { LicenseItem } from "#/apis/infra/listLicenses";
import OrgEditModal from "./OrgEditModal";

// TanStack cell context (we need row.original, richer than the app's TableCellInfo).
type CellInfo = { row: { original: LicenseItem } };

const OrgName = styled.div`
  color: var(--x-gray-200, #f6f6f7); font-weight: 600;
  .id { color: var(--x-gray-500, #a9a9ac); font-weight: 400; font-size: 11.5px; }
`;
const Muted = styled.span`color: var(--x-gray-400, #cfd0d3); font-size: 12px;`;
// Role as plain table text (matches xcockpit's role column — no pill badge).
const RoleText = styled.span`color: var(--x-gray-200, #f6f6f7); font-size: 13px;`;
const Empty = styled.div`color: var(--x-gray-500, #a9a9ac); font-size: 13px; padding: 24px 4px;`;

// xcockpit-style row action: a borderless gear (Settings) icon button, hover-highlighted.
const GearBtn = styled.button`
  display: inline-flex; align-items: center; justify-content: center;
  width: 30px; height: 30px; padding: 0;
  background: none; border: none; border-radius: 4px; cursor: pointer;
  &:hover { background: var(--x-purple-500, #494358); }
`;

// xcockpit Status tag: ACTIVE = teal (bg-info) + dark text, INACTIVE = grey (bg-secondary) + light.
const StatusBadge = styled.span<{ $on: boolean }>`
  display: inline-block; font-size: 10.5px; font-weight: 700; letter-spacing: .03em;
  text-transform: uppercase; border-radius: 4px; padding: 3px 9px;
  color: ${(p: { $on: boolean }) => (p.$on ? "var(--bs-dark, #1e2229)" : "var(--bs-white, #f6f6f7)")};
  background: ${(p: { $on: boolean }) => (p.$on ? "var(--bs-info, #34d7e6)" : "var(--bs-secondary, #5a5566)")};
`;

// Date as monospace YYYY-MM-DD (matches xcockpit's date columns); red when expired.
const Expiry = styled.span<{ $bad: boolean }>`
  font-family: "Roboto Mono", monospace; font-size: 12.5px;
  color: ${(p: { $bad: boolean }) => (p.$bad ? "var(--x-red, #ff595a)" : "var(--x-gray-300, #e9e9eb)")};
`;

// License status enum uses "enable"/"disable" (distinct from users, which use "active"/"disable").
const isEnabled = (status: string): boolean => status === "enable";

const isExpired = (iso: string): boolean => {
  if (!iso) return false;
  const ms = Date.parse(iso);
  return Number.isFinite(ms) && ms < Date.now();
};
const fmtDate = (iso: string): string => (iso ? iso.slice(0, 10) : "—");

// Organizations (= internal LICENSE) management list. SYSTEM_ADMIN can edit status/expiry via
// OrgEditModal; other managers see it read-only. Data comes from the caller's role-scoped org list.
const OrganizationsTab = ({ rbac }: { rbac: Rbac }): JSX.Element => {
  const { t } = useTranslation("management");
  const { orgs, refreshOrgs } = useOrg();
  const [editOrg, setEditOrg] = useState<LicenseItem | null>(null);
  // Column-header filters — all searchable Select dropdowns (xui HeaderSelectFilter).
  const [nameFilter, setNameFilter] = useState("");
  const [territoryFilter, setTerritoryFilter] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  // Every Organization, for the Organization-name column dropdown.
  const nameOptions = useMemo<SelectOption[]>(
    () => orgs.map((o) => ({ label: o.license_name, value: o.license_id, textValue: o.license_name })),
    [orgs],
  );

  // Distinct Territories present in the org list, for the Territory column dropdown filter.
  const territoryOptions = useMemo<SelectOption[]>(() => {
    const seen = new Map<string, string>();
    orgs.forEach((o) => { if (!seen.has(o.customer_id)) seen.set(o.customer_id, o.customer_name); });
    return Array.from(seen, ([value, label]) => ({ label, value, textValue: label }));
  }, [orgs]);

  // Distinct effective roles present (the caller's role on each org), for the Role column filter.
  const roleOptions = useMemo<SelectOption[]>(() => {
    const seen = new Set<string>();
    orgs.forEach((o) => { if (o.effective_role) seen.add(o.effective_role); });
    return Array.from(seen, (r) => ({ label: r, value: r, textValue: r }));
  }, [orgs]);

  const statusOptions: SelectOption[] = [
    { label: t("status.enabled"), value: "enabled", textValue: t("status.enabled") },
    { label: t("status.disabled"), value: "disabled", textValue: t("status.disabled") },
  ];

  const filtered = useMemo(() => orgs.filter((o) => {
    if (nameFilter && o.license_id !== nameFilter) return false;
    if (territoryFilter && o.customer_id !== territoryFilter) return false;
    if (roleFilter && o.effective_role !== roleFilter) return false;
    if (statusFilter === "enabled" && !isEnabled(o.license_status)) return false;
    if (statusFilter === "disabled" && isEnabled(o.license_status)) return false;
    return true;
  }), [orgs, nameFilter, territoryFilter, roleFilter, statusFilter]);

  const columns = [
    {
      id: "name",
      accessorKey: "license_name",
      header: t("orgList.colName"),
      meta: {
        filterComponent: (f: FilterComponentProps) => (
          <HeaderSelectFilter
            {...f}
            isMulti={false}
            options={nameOptions}
            onChange={(vals: string[]) => setNameFilter(vals[0] ?? "")}
          />
        ),
      },
      cell: (info: CellInfo) => {
        const o = info.row.original;
        return (
          <OrgName>
            {o.license_name}
            <div className="id">{o.license_id}</div>
          </OrgName>
        );
      },
    },
    {
      id: "territory",
      accessorKey: "customer_name",
      header: t("scope.territory"),
      meta: {
        filterComponent: (f: FilterComponentProps) => (
          <HeaderSelectFilter
            {...f}
            isMulti={false}
            options={territoryOptions}
            onChange={(vals: string[]) => setTerritoryFilter(vals[0] ?? "")}
          />
        ),
      },
      cell: (info: CellInfo) => <Muted>{info.row.original.customer_name}</Muted>,
    },
    {
      id: "role",
      accessorKey: "effective_role",
      header: t("orgList.colRole"),
      meta: {
        filterComponent: (f: FilterComponentProps) => (
          <HeaderSelectFilter
            {...f}
            isMulti={false}
            options={roleOptions}
            onChange={(vals: string[]) => setRoleFilter(vals[0] ?? "")}
          />
        ),
      },
      cell: (info: CellInfo) => <RoleText>{info.row.original.effective_role}</RoleText>,
    },
    {
      id: "status",
      accessorKey: "license_status",
      header: t("orgList.colStatus"),
      size: 150,
      meta: {
        filterComponent: (f: FilterComponentProps) => (
          <HeaderSelectFilter
            {...f}
            isMulti={false}
            options={statusOptions}
            onChange={(vals: string[]) => setStatusFilter(vals[0] ?? "")}
          />
        ),
      },
      cell: (info: CellInfo) => {
        const on = isEnabled(info.row.original.license_status);
        return <StatusBadge $on={on}>{on ? t("status.enabled") : t("status.disabled")}</StatusBadge>;
      },
    },
    {
      id: "expiry",
      accessorKey: "expires_at",
      header: t("orgList.colExpires"),
      size: 170,
      cell: (info: CellInfo) => {
        const { expires_at: exp } = info.row.original;
        return <Expiry $bad={isExpired(exp)}>{fmtDate(exp)}</Expiry>;
      },
    },
    ...(rbac.canEditOrg ? [{
      id: "actions",
      header: "",
      size: 80,
      cell: (info: CellInfo) => (
        <GearBtn type="button" title={t("orgList.edit")} onClick={() => setEditOrg(info.row.original)}>
          <Settings size={16} color="var(--x-gray-400, #cfd0d3)" />
        </GearBtn>
      ),
    }] : []),
  ];

  return (
    <>
      {orgs.length === 0
        ? <Empty>{t("orgList.empty")}</Empty>
        : <TableV8 data={filtered} loading={false} columns={columns} pagination={{ pageSize: 10 }} />}
      <OrgEditModal
        show={!!editOrg}
        org={editOrg}
        onClose={() => setEditOrg(null)}
        onSaved={refreshOrgs}
      />
    </>
  );
};

export default OrganizationsTab;
